import { cva, type VariantProps } from "class-variance-authority";
import { CircleAlert, CircleCheck, Info, TriangleAlert, type LucideIcon } from "lucide-react";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Banner de aviso dentro de una pantalla o un dialogo.
 *
 * El texto es SIEMPRE `text-foreground`: el tono lo llevan el borde y el icono.
 * `--warning` como texto sobre su propio tinte no llega al contraste minimo
 * (MASTER seccion 2.1), y asi el aviso cumple en las dos superficies sin
 * verificar caso por caso.
 */
const avisoVariants = cva(
  "flex items-start gap-2 rounded-lg border text-sm text-foreground [&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      tono: {
        info: "border-info/40 bg-info/10 [&>svg]:text-info",
        warning: "border-warning/40 bg-warning/10 [&>svg]:text-warning",
        destructive: "border-destructive/40 bg-destructive/10 [&>svg]:text-destructive",
        success: "border-success/40 bg-success/10 [&>svg]:text-success",
        neutral: "border-border bg-muted/50 [&>svg]:text-muted-foreground",
      },
      compacto: {
        true: "px-3 py-2 text-xs [&>svg]:size-3.5",
        false: "p-3",
      },
    },
    defaultVariants: { tono: "info", compacto: false },
  },
);

type Tono = NonNullable<VariantProps<typeof avisoVariants>["tono"]>;

const ICONOS: Record<Tono, LucideIcon> = {
  info: Info,
  warning: TriangleAlert,
  destructive: CircleAlert,
  success: CircleCheck,
  neutral: Info,
};

export interface AvisoProps extends HTMLAttributes<HTMLDivElement> {
  tono?: Tono;
  titulo?: string;
  /** Sobrescribe el icono por defecto del tono; `null` lo quita. */
  icono?: LucideIcon | null;
  accion?: ReactNode;
  /** Version de una linea para dentro de un formulario. */
  compacto?: boolean;
  children?: ReactNode;
}

export function Aviso({
  tono = "info",
  titulo,
  icono,
  accion,
  compacto = false,
  className,
  children,
  ...props
}: AvisoProps): JSX.Element {
  const Icono = icono === undefined ? ICONOS[tono] : icono;
  return (
    <div
      // Lo destructivo interrumpe; el resto se anuncia cuando el lector llega.
      role={tono === "destructive" ? "alert" : "status"}
      className={cn(avisoVariants({ tono, compacto }), className)}
      {...props}
    >
      {Icono ? <Icono aria-hidden="true" /> : null}
      <div className="min-w-0 flex-1">
        {titulo ? <p className="font-medium">{titulo}</p> : null}
        {children !== undefined && children !== null ? <div>{children}</div> : null}
        {accion ? <div className="mt-2">{accion}</div> : null}
      </div>
    </div>
  );
}

export { avisoVariants };
